import React, { useCallback, useEffect, useState } from 'react';
import { FaSpinner, FaBell } from 'react-icons/fa';
import { auth } from '../firebase';

const ANALYTICS_API = 'https://api.vuebillboards.com';
const labelClass = 'mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500';

const emptyForm = {
  title: '',
  body: '',
  imageUrl: '',
  sendAtLocal: '',
  audience: 'ios',
};

async function authHeaders() {
  const user = auth.currentUser;
  if (!user) throw new Error('Not signed in');
  const token = await user.getIdToken();
  return {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
}

function localInputToIso(localValue) {
  if (!localValue) return '';
  const d = new Date(localValue);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString();
}

function formatWhen(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString();
}

function statusStyles(status) {
  switch (status) {
    case 'scheduled':
      return 'border-sky-200 bg-sky-50 text-sky-900';
    case 'sending':
      return 'border-amber-200 bg-amber-50 text-amber-900';
    case 'sent':
      return 'border-emerald-200 bg-emerald-50 text-emerald-900';
    case 'failed':
      return 'border-rose-200 bg-rose-50 text-rose-900';
    case 'cancelled':
      return 'border-slate-200 bg-slate-50 text-slate-600';
    default:
      return 'border-slate-200 bg-slate-50 text-slate-700';
  }
}

export default function ScheduledPushPage() {
  const [form, setForm] = useState(emptyForm);
  const [notifications, setNotifications] = useState([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [busyId, setBusyId] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const headers = await authHeaders();
      const qs = statusFilter ? `?status=${encodeURIComponent(statusFilter)}&limit=100` : '?limit=100';
      const res = await fetch(`${ANALYTICS_API}/api/admin/scheduled-push${qs}`, { headers });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || !json.success) {
        throw new Error(json.error || `Failed to load (${res.status})`);
      }
      setNotifications(Array.isArray(json.notifications) ? json.notifications : []);
    } catch (e) {
      setError(e.message || 'Failed to load scheduled pushes');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    load();
  }, [load]);

  async function handleCreate(e) {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const sendAt = localInputToIso(form.sendAtLocal);
      if (!sendAt) throw new Error('Choose a valid send time');
      const headers = await authHeaders();
      const res = await fetch(`${ANALYTICS_API}/api/admin/scheduled-push`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          title: form.title.trim(),
          body: form.body.trim(),
          imageUrl: form.imageUrl.trim() || undefined,
          sendAt,
          audience: form.audience || 'ios',
        }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || !json.success) {
        throw new Error(json.error || `Create failed (${res.status})`);
      }
      setForm(emptyForm);
      setSuccess('Notification scheduled');
      await load();
    } catch (err) {
      setError(err.message || 'Failed to schedule');
    } finally {
      setSaving(false);
    }
  }

  async function handleCsvFile(file) {
    if (!file) return;
    setImporting(true);
    setError('');
    setSuccess('');
    try {
      const csv = await file.text();
      const headers = await authHeaders();
      const res = await fetch(`${ANALYTICS_API}/api/admin/scheduled-push/bulk`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ csv }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok && res.status !== 207) {
        throw new Error(json.error || `Import failed (${res.status})`);
      }
      const created = json.createdCount || 0;
      const failed = json.failedCount || 0;
      setSuccess(`Imported ${created} notification(s)${failed ? `, ${failed} failed` : ''}`);
      if (failed && Array.isArray(json.failed) && json.failed.length) {
        const detail = json.failed
          .slice(0, 3)
          .map((f) => `Row ${f.index + 1}: ${f.error}`)
          .join('; ');
        setError(detail);
      }
      await load();
    } catch (err) {
      setError(err.message || 'CSV import failed');
    } finally {
      setImporting(false);
    }
  }

  async function cancelOne(id) {
    setBusyId(id);
    setError('');
    try {
      const headers = await authHeaders();
      const res = await fetch(`${ANALYTICS_API}/api/admin/scheduled-push/${encodeURIComponent(id)}`, {
        method: 'DELETE',
        headers,
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || !json.success) throw new Error(json.error || 'Cancel failed');
      await load();
    } catch (err) {
      setError(err.message || 'Cancel failed');
    } finally {
      setBusyId('');
    }
  }

  async function sendNow(id) {
    if (!window.confirm('Send this notification immediately to all iOS topic subscribers?')) return;
    setBusyId(id);
    setError('');
    setSuccess('');
    try {
      const headers = await authHeaders();
      const res = await fetch(
        `${ANALYTICS_API}/api/admin/scheduled-push/${encodeURIComponent(id)}/send-now`,
        { method: 'POST', headers }
      );
      const json = await res.json().catch(() => ({}));
      if (!res.ok || !json.success) throw new Error(json.error || 'Send failed');
      setSuccess('Send triggered');
      await load();
    } catch (err) {
      setError(err.message || 'Send failed');
    } finally {
      setBusyId('');
    }
  }

  const scheduledCount = notifications.filter((n) => n.status === 'scheduled').length;

  return (
    <div className="f-page-enter mx-auto max-w-[1920px] px-4 pb-10 md:px-6">
      <div className="f-header rounded-xl border border-slate-200/80 shadow-sm">
        <div>
          <h1 className="f-title">Scheduled Push</h1>
          <p className="f-subtitle mb-0">
            Schedule iOS push notifications (title, description, image). Default audience is all iOS via topic{' '}
            <code className="rounded bg-slate-100 px-1 text-xs">all_ios</code>.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3 text-sm text-slate-600">
          <span className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-1.5 font-medium tabular-nums">
            {notifications.length} shown
          </span>
          <span className="rounded-lg border border-sky-200 bg-sky-50 px-3 py-1.5 font-medium text-sky-900 tabular-nums">
            {scheduledCount} scheduled
          </span>
        </div>
      </div>

      {error && (
        <div
          className="mb-6 flex items-center justify-between gap-4 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-900"
          role="alert"
        >
          <span>{error}</span>
          <button
            type="button"
            className="f-button f-button-secondary shrink-0 !min-h-8 !h-8 !px-3 !text-xs"
            onClick={() => setError('')}
          >
            Dismiss
          </button>
        </div>
      )}

      {success && (
        <div className="mb-6 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900">
          {success}
        </div>
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,420px)_1fr] lg:items-start">
        <div className="space-y-6">
          <div className="f-card space-y-5">
            <div className="border-b border-slate-100 pb-4">
              <h2 className="flex items-center gap-2 text-base font-semibold text-slate-900">
                <FaBell className="text-sky-500" />
                Schedule one
              </h2>
              <p className="mt-1 text-xs text-slate-500">
                Image must be a public https URL. Send time uses your local timezone.
              </p>
            </div>

            <form onSubmit={handleCreate} className="flex flex-col gap-4">
              <div>
                <label className={labelClass} htmlFor="push-title">
                  Title
                </label>
                <input
                  id="push-title"
                  className="f-input w-full"
                  required
                  maxLength={200}
                  value={form.title}
                  onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                  placeholder="Notification title"
                />
              </div>

              <div>
                <label className={labelClass} htmlFor="push-body">
                  Description
                </label>
                <textarea
                  id="push-body"
                  className="f-input w-full min-h-[100px]"
                  required
                  maxLength={2000}
                  value={form.body}
                  onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))}
                  placeholder="Notification body / description"
                />
              </div>

              <div>
                <label className={labelClass} htmlFor="push-image">
                  Image URL
                </label>
                <input
                  id="push-image"
                  className="f-input w-full"
                  type="url"
                  value={form.imageUrl}
                  onChange={(e) => setForm((f) => ({ ...f, imageUrl: e.target.value }))}
                  placeholder="https://…"
                />
              </div>

              <div>
                <label className={labelClass} htmlFor="push-when">
                  Send at
                </label>
                <input
                  id="push-when"
                  className="f-input w-full"
                  type="datetime-local"
                  required
                  value={form.sendAtLocal}
                  onChange={(e) => setForm((f) => ({ ...f, sendAtLocal: e.target.value }))}
                />
              </div>

              <div>
                <label className={labelClass} htmlFor="push-audience">
                  Audience
                </label>
                <select
                  id="push-audience"
                  className="f-input w-full"
                  value={form.audience}
                  onChange={(e) => setForm((f) => ({ ...f, audience: e.target.value }))}
                >
                  <option value="ios">All iOS (topic all_ios)</option>
                  <option value="all_with_token">All users with FCM token</option>
                </select>
              </div>

              <button type="submit" className="f-button f-button-primary" disabled={saving}>
                {saving ? (
                  <>
                    <FaSpinner className="animate-spin" /> Scheduling…
                  </>
                ) : (
                  'Schedule notification'
                )}
              </button>
            </form>
          </div>

          <div className="f-card space-y-4">
            <div className="border-b border-slate-100 pb-4">
              <h2 className="text-base font-semibold text-slate-900">Import CSV</h2>
              <p className="mt-1 text-xs text-slate-500">
                Columns: <code>title</code>, <code>body</code> (or <code>description</code>),{' '}
                <code>imageUrl</code>, <code>sendAt</code> (ISO UTC). Optional <code>audience</code>.
              </p>
            </div>
            <input
              type="file"
              accept=".csv,text/csv"
              className="block w-full text-sm text-slate-600 file:mr-3 file:rounded-lg file:border-0 file:bg-slate-900 file:px-3 file:py-2 file:text-sm file:font-medium file:text-white hover:file:bg-slate-800"
              disabled={importing}
              onChange={(e) => {
                const file = e.target.files?.[0];
                e.target.value = '';
                handleCsvFile(file);
              }}
            />
            {importing && (
              <p className="flex items-center gap-2 text-sm text-slate-600">
                <FaSpinner className="animate-spin" /> Importing…
              </p>
            )}
          </div>
        </div>

        <div className="f-card space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 pb-4">
            <div>
              <h2 className="text-base font-semibold text-slate-900">Scheduled & recent</h2>
              <p className="mt-1 text-xs text-slate-500">Cron sends due items about every 60 seconds.</p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <select
                className="f-input !min-h-9 !py-1.5 text-sm"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="">All statuses</option>
                <option value="scheduled">Scheduled</option>
                <option value="sent">Sent</option>
                <option value="failed">Failed</option>
                <option value="cancelled">Cancelled</option>
              </select>
              <button type="button" className="f-button f-button-secondary !min-h-9 !px-3 !text-xs" onClick={load}>
                Refresh
              </button>
            </div>
          </div>

          {loading ? (
            <div className="flex items-center gap-2 py-10 text-sm text-slate-500">
              <FaSpinner className="animate-spin" /> Loading…
            </div>
          ) : notifications.length === 0 ? (
            <p className="py-10 text-center text-sm text-slate-500">No scheduled push notifications yet.</p>
          ) : (
            <ul className="divide-y divide-slate-100">
              {notifications.map((n) => (
                <li key={n.id} className="flex flex-col gap-3 py-4 sm:flex-row sm:items-start sm:justify-between">
                  <div className="min-w-0 flex-1 space-y-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-semibold text-slate-900">{n.title}</span>
                      <span
                        className={`rounded-md border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${statusStyles(n.status)}`}
                      >
                        {n.status}
                      </span>
                    </div>
                    <p className="text-sm text-slate-600 whitespace-pre-wrap">{n.body}</p>
                    <p className="text-xs text-slate-500">
                      Send at {formatWhen(n.sendAt)} · {n.audience}
                      {n.topic ? ` · ${n.topic}` : ''}
                      {n.source ? ` · via ${n.source}` : ''}
                    </p>
                    {n.imageUrl && (
                      <a
                        href={n.imageUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="mt-1 inline-block overflow-hidden rounded-lg border border-slate-200"
                      >
                        <img src={n.imageUrl} alt="" className="h-16 w-auto max-w-[200px] object-cover" />
                      </a>
                    )}
                    {n.error && <p className="text-xs text-rose-600">{n.error}</p>}
                  </div>
                  {n.status === 'scheduled' && (
                    <div className="flex shrink-0 flex-wrap gap-2">
                      <button
                        type="button"
                        className="f-button f-button-primary !min-h-8 !px-3 !text-xs"
                        disabled={busyId === n.id}
                        onClick={() => sendNow(n.id)}
                      >
                        Send now
                      </button>
                      <button
                        type="button"
                        className="f-button f-button-secondary !min-h-8 !px-3 !text-xs"
                        disabled={busyId === n.id}
                        onClick={() => cancelOne(n.id)}
                      >
                        Cancel
                      </button>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
